import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { designSystem } from '@yola/ws-ui';
import {
  dialogs,
  contentEditable,
  blocks,
  view,
  hdrm,
  i18next,
  customization,
  template,
} from '@yola/ws-sdk';
import isTouchSupport from 'src/js/modules/control-pane/helpers/is-touch-support';
import withSelection from 'src/js/modules/utils/with-selection';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import segment from 'src/js/modules/analytics/segment';
import dialogCallSourceIds from 'src/js/modules/website-design/constants/dialog-call-source-ids';
import fontSettingsSourceIds from 'src/js/modules/website-design/constants/font-settings-source-ids';
import { elementTypeIds } from 'src/js/modules/website-design/constants/element-customization';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import helpers from 'src/js/modules/website-design/helpers';
import actions from 'src/js/modules/website-design/actions';
import usePaneSelectHeight from 'src/js/modules/control-pane/helpers/use-pane-select-height';
import useElementTypeSelect from '../helpers/use-element-type-select';
import changeElementsType from '../helpers/change-elements-type';
import PreviewOption from './preview-option';
import constants from '../constants';

const { applyFontPalette, persistTextEditingFontPairChanges } = helpers;

const { SimpleSelect, IconButton, HorizontalStack } = designSystem;
const { text: hdrmText } = hdrm.injections.bindings;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const MAP_ELEMENT_TYPE_TO_READABLE_NAME = {
  p: 'Normal text',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  blockquote: 'Quote',
};

const MAP_ELEMENT_VALUE_TO_SECTION = {
  p: elementTypeIds.PARAGRAPH,
  h1: elementTypeIds.HEADING_1,
  h2: elementTypeIds.HEADING_2,
  h3: elementTypeIds.HEADING_3,
  h4: elementTypeIds.HEADING_4,
  h5: elementTypeIds.HEADING_5,
  h6: elementTypeIds.HEADING_6,
  blockquote: elementTypeIds.QUOTE,
};

const MAX_MENU_HEIGHT = 466;

const isDesktop = !isTouchSupport();

const ElementTypeValue = ({ children: { text } }) => text;

const ElementTypeOption = ({ children: { text, icon = null, type }, onSettingsClick = null }) => {
  if (!type) return <div className="ws-element-type-preview--option">{text}</div>;

  return (
    <div className="ws-element-type-option">
      <HorizontalStack verticalAlign="center" align="space-between">
        <PreviewOption type={type}>
          {icon && <i className="ws-quote-mark-preview" style={{ backgroundImage: icon }} />}
          {text}
        </PreviewOption>

        {onSettingsClick && isDesktop && (
          <span className="ws-element-type-option__settings-button">
            <IconButton
              label={i18next.t('Edit style')}
              transparent
              glyph="slider-settings-vertical"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onSettingsClick();
              }}
            />
          </span>
        )}
      </HorizontalStack>
    </div>
  );
};

function ElementTypeSelect({ elementId, getContext }) {
  // We need `isFocused` state just to force component's rerender
  // to fix problem with element's type changing when `ws-text` is empty
  const [, setIsFocused] = useState(false);
  const { elementType, focusElements, selection, elementTypeOptions } =
    useElementTypeSelect(elementId);
  const { scrollPosition, controlPane } = getContext();
  usePaneSelectHeight({ offsetY: controlPane.position.y, scrollPosition });

  const dispatch = useDispatch();
  const currentFontPair = useSelector(customization.accessors.getCurrentFontPair);
  const fonts = useSelector(customization.selectors.getFontPairs);
  const initialCustomFonts = useSelector(customization.selectors.getCustomFontPairs);
  const customFontsTemplate = useSelector(customization.selectors.getFontPairTemplate);
  const fontsVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.FONTS_VARIABLE)
  );

  const [featureFlags] = useFeatureFlags(['advanced_font_customization']);
  const { advanced_font_customization: isAdvancedFontCustomizationFeatureFlagEnabled } =
    featureFlags;

  const openFontSettingsDialog = useCallback(
    (optionValue) => {
      const initialActiveSection = MAP_ELEMENT_VALUE_TO_SECTION[optionValue];

      if (!initialActiveSection) {
        return;
      }

      const activeFontPairId = currentFontPair?.id;

      if (!activeFontPairId) {
        return;
      }

      const allFonts = [...fonts, ...initialCustomFonts];

      document.activeElement?.blur();

      dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG, {
        fontPairId: activeFontPairId,
        sourceId: fontSettingsSourceIds.TEXT_EDITING,
        initialActiveSection,
        preserveControlPane: true,
        initialSharedData: {
          activeFontPairId,
          customFonts: initialCustomFonts,
          allFonts,
          applyCustomFontPair: (params) =>
            applyFontPalette({
              ...params,
              allFonts,
              currentFontPair,
              fontsVariable,
              customFontsTemplate,
            }),
          persistFontPairChangesForTextEditing: (updatedCustomFontsList, newConfig) =>
            persistTextEditingFontPairChanges({
              storeCustomFonts: initialCustomFonts,
              updatedCustomFontsList,
              newConfig,
              fontsVariable,
              customFontsTemplate,
              onFontPairsChanged: () => dispatch(actions.changeFontPairs()),
            }),
        },
      });
    },
    [currentFontPair, fonts, initialCustomFonts, fontsVariable, customFontsTemplate, dispatch]
  );

  const selectOptions = useMemo(() => {
    if (!isAdvancedFontCustomizationFeatureFlagEnabled) {
      return elementTypeOptions;
    }

    return elementTypeOptions.map((option) => {
      if (!MAP_ELEMENT_VALUE_TO_SECTION[option.value]) {
        return option;
      }

      return {
        ...option,
        onSettingsClick: () => openFontSettingsDialog(option.value),
      };
    });
  }, [elementTypeOptions, openFontSettingsDialog, isAdvancedFontCustomizationFeatureFlagEnabled]);

  const handleSelect = (newType) => {
    if (!newType) return;

    if (newType.value === constants.common.CUSTOMIZE_FONTS_HOTKEY) {
      dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
        sourceId: dialogCallSourceIds.CUSTOMIZE_FONTS,
      });
      return;
    }
    contentEditable.operations.addCheckpoint();
    const currentLiveElement = view.accessors.getLiveElement(elementId);
    const contentEditableElement = hdrmText.getRootTextElement(currentLiveElement);
    changeElementsType(newType, focusElements, selection, elementId);

    controlPane.forceHighlighterRerender();

    const allBlocks = view.accessors.getPageBlocks();
    const element = allBlocks.find((block) => block.contains(contentEditableElement));
    trackEvent(events.TEXT_ELEMENT_TYPE_SELECTED, {
      blockId: blocks.accessors.getBlockIdByElement(element),
      blockVariationId: blocks.accessors.getVariationIdByElement(element),
      elementType: MAP_ELEMENT_TYPE_TO_READABLE_NAME[newType.value],
    });
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="ws-element-type-select">
      <SimpleSelect
        options={selectOptions}
        components={{ Option: ElementTypeOption, SingleValue: ElementTypeValue }}
        onChange={handleSelect}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={elementType}
        size="medium"
        maxMenuHeight={MAX_MENU_HEIGHT}
      />
    </div>
  );
}

ElementTypeValue.propTypes = {
  children: PropTypes.node.isRequired,
};

ElementTypeOption.propTypes = {
  children: PropTypes.node.isRequired,
  onSettingsClick: PropTypes.func,
};

ElementTypeSelect.propTypes = {
  elementId: PropTypes.string.isRequired,
  getContext: PropTypes.func.isRequired,
};

export default withSelection(ElementTypeSelect);
